import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  createEmployee,
  updateEmployee,
  getEmployee
} from "../../services/employeeService";
import { getDepartments ,getDesignations} from "../../services/departmentService";
import { getCountries } from "../../services/countryService";
import {
  Form,
  Button,
  Row,
  Col,
  Card,
  Container,
  Spinner,
  Toast,
  ToastContainer,
  Image
} from "react-bootstrap";
import Layout from "../../components/Layout";
import TopBar from "../../components/TopBar";
import { FaFilePdf, FaImage, FaTimes, FaCamera, FaUpload, FaSave } from 'react-icons/fa';
import { baseURL, imageUrl } from "../../services/api";
import moment from 'moment';

const EmployeeForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    name: "",
    email: "",
    personal_email: "",
    phone: "",
    dob: "",
    address: "",
    status: "active",
    notice_period_start_date: "",
    notice_period_days: 30,
    joining_date: "",
    department_id: "", // Default to first department
    designation_id: "", // Default to first designation
    country_id: "", // Add country field
    profile_image: null,
    employee_type: "", // Default employee type
    expiry_date: "", // Expiry date for contract employees
    duration_value: 1,
    duration_unit: 'months',
    pf_enabled: false, // PF enabled flag
    // Salary fields
    salary_type: "",
    base_salary: "",
    hourly_rate: "",
    over_time_rate: "",
    payment_method: "",
    ids: [{ type: "", number: "", image: null, expiry_date: "" }]
  });

  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({ show: false, message: "", variant: "success" });
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(true);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [designations, setDesignations] = useState([]);
  const [loadingDesignations, setLoadingDesignations] = useState(true);

  useEffect(() => {
    // Load departments, designations and countries
    setLoadingDepartments(true);
    setLoadingDesignations(true);
    setLoadingCountries(true);
    
    Promise.all([
      getDepartments(),
      getDesignations(),
      getCountries()
    ]).then(([deptRes, designationRes, countryRes]) => {
      setDesignations(designationRes?.data || []);
      setDepartments(deptRes || []);
      setCountries(countryRes?.data || []);
    }).catch(error => {
      console.error('Error loading dropdown data:', error);
      setToast({
        show: true,
        message: 'Failed to load dropdown data. Please try again.',
        variant: 'danger'
      });
    }).finally(() => {
      setLoadingDepartments(false);
      setLoadingDesignations(false);
      setLoadingCountries(false);
    });

    if (isEdit) {
      getEmployee(id).then((res) => {
        const emp = res.data;
        const formData = {
          ...emp,
          profile_image: emp.profile_image ? emp.profile_image : null,
          joining_date: emp.joining_date ? moment(emp.joining_date).format('YYYY-MM-DD') : '',
          expiry_date: emp.expiry_date ? moment(emp.expiry_date).format('YYYY-MM-DD') : '',
          dob: emp.dob ? moment(emp.dob).format('YYYY-MM-DD') : '',
          notice_period_start_date: emp.notice_period_start_date ? moment(emp.notice_period_start_date).format('YYYY-MM-DD') : '',
          notice_period_days: emp.notice_period_days || 30,

          ids: emp.ids ? emp.ids.map(id => ({ 
            ...id,
            image: id.image ? id.image : null,
            expiry_date: id.expiry_date ? id.expiry_date.split('T')[0] : ''
          })) : []
        };
        
        setForm(formData);
        
        // If duration fields are not set but expiry date is, calculate them
        if (formData.expiry_date && (!formData.duration_value || !formData.duration_unit)) {
          const expiryMoment = moment(formData.expiry_date);
          const now = moment();
          
          if (expiryMoment.isAfter(now)) {
            const duration = moment.duration(expiryMoment.diff(now));
            const days = Math.ceil(duration.asDays());
            const months = Math.ceil(duration.asMonths());
            const years = Math.ceil(duration.asYears());
            
            formData.duration_value = years > 1 ? years : (months > 1 ? months : (days || 1));
            formData.duration_unit = years > 1 ? 'years' : (months > 1 ? 'months' : 'days');
          }
        }
      });
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    
    // For date fields, handle them properly
    if (name === 'dob' || name === 'joining_date' || name === 'expiry_date') {
      // Use the raw value from the date input (already in YYYY-MM-DD format)
      setForm(prev => ({
        ...prev,
        [name]: value // Don't set to empty string if value is falsy, keep as is
      }));
    } else if (name === 'employee_type') {
      // If employee type changes to permanent, clear expiry date
      setForm(prev => ({
        ...prev,
        [name]: value,
        ...(value === 'Permanent' && { expiry_date: '' })
      }));
    } else {
      setForm(prev => ({
        ...prev,
        [name]: type === 'file' ? files[0] : value
      }));
    }
  };
  
  const handleDurationChange = (e) => {
    const { name, value } = e.target;
    const fieldName = name === 'value' ? 'duration_value' : 'duration_unit';
    const fieldValue = name === 'value' ? parseInt(value) || 1 : value;
    
    setForm(prev => {
      const updatedForm = {
        ...prev,
        [fieldName]: fieldValue
      };
      
      // Calculate expiry date based on duration if both fields are set
      if (updatedForm.duration_value > 0 && updatedForm.duration_unit) {
        const expiryDate = moment()
          .add(updatedForm.duration_value, updatedForm.duration_unit)
          .format('YYYY-MM-DD');
        
        updatedForm.expiry_date = expiryDate;
      }
      
      return updatedForm;
    });
  };
  


  const handleFileChange = (e) => {
    // Only update if a file is selected
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const maxSize = 1 * 1024 * 1024; // 1MB
      if (file.size > maxSize) {
        showToast('Profile image size should be less than 1MB', 'danger');
        return;
      }
      setForm({ ...form, profile_image: file });
    }
  };

  const handleIDChange = (index, e) => {
    const newIds = [...form.ids];
    const { name, value, files } = e.target;
    
    // Create a new object to ensure React detects the change
    const updatedId = { ...newIds[index] };
    
    if (name === 'image') {
      if (files && files[0]) {
        // Validate file type
        const validFileTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
        if (!validFileTypes.includes(files[0].type)) {
          showToast('Please upload a valid file (JPEG, JPG, PNG, PDF)', 'danger');
          return;
        }
        
        // Validate file size (max 5MB for PDFs, 2MB for images)
        const maxSize = files[0].type === 'application/pdf' ? 5 * 1024 * 1024 : 5 * 1024 * 1024;
        if (files[0].size > maxSize) {
          const maxSizeMB = maxSize / (1024 * 1024);
          showToast(`File size should be less than ${maxSizeMB}MB`, 'danger');
          return;
        }
        
        // Store file type for display purposes
        updatedId.fileType = files[0].type;
        updatedId.fileName = files[0].name;
        updatedId.image = files[0];
      }
    } else {
      updatedId[name] = value;
    }
    
    newIds[index] = updatedId;
    setForm({ ...form, ids: newIds });
  };

  const handleExpiryDateChange = (index, e) => {
    const newIds = [...form.ids];
    newIds[index].expiry_date = e.target.value;
    setForm({ ...form, ids: newIds });
  };

  const addID = () => {
    setForm({ ...form, ids: [...form.ids, { type: "", number: "", image: null, expiry_date: "" }] });
  };

  const removeID = (index) => {
    const newIds = [...form.ids];
    newIds.splice(index, 1);
    setForm({ ...form, ids: newIds });
  };

  const showToast = (message, variant = "success") => {
    setToast({ show: true, message, variant });
    setTimeout(() => setToast({ ...toast, show: false }), 3000);
  };

  // Function to check if a string is a valid URL
  const isUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic frontend validation
    const localErrors = {};
    if (!form.name) localErrors.name = "Name is required";
    if (!form.email) localErrors.email = "Email is required";
    if (!form.emp_code) localErrors.emp_code = "Employee Code is required";
    if (!form.employee_type) localErrors.employee_type = "Employee Type is required";
    if (!form.designation_id) localErrors.designation_id = "Designation is required";

    // Validate duration fields for non-permanent employees
    if (form.employee_type && form.employee_type !== 'Permanent') {
      if (!form.duration_value || form.duration_value < 1) {
        localErrors.duration_value = "Duration value must be at least 1";
      }
      if (!form.duration_unit || !['days', 'months', 'years'].includes(form.duration_unit)) {
        localErrors.duration_unit = "Please select a valid duration unit";
      }
      if (!form.expiry_date) {
        localErrors.expiry_date = "Expiry date is required for non-permanent employees";
      }
    }

    // if (!form.phone) localErrors.phone = "Phone is required";
    // if (form.dob && !/^\d{4}-\d{2}-\d{2}$/.test(form.dob)) {
    //   localErrors.dob = "Date of Birth must be in YYYY-MM-DD format";
    // }
    // if (form.joining_date && !/^\d{4}-\d{2}-\d{2}$/.test(form.joining_date)) {
    //   localErrors.joining_date = "Joining Date must be in YYYY-MM-DD format";
    // }
    // if (!form.department_id) localErrors.department_id = "Department is required";
    // if (!form.country_id) localErrors.country_id = "Country is required";
    
    if (Object.keys(localErrors).length > 0) {
      setErrors(localErrors);
      showToast("Please fix validation errors", "danger");
      return;
    }

  
    const fd = new FormData();
    console.log(form)
    // Only append fields that have values
    Object.entries(form).forEach(([key, value]) => {
      console.log(key, value)
      if (key === "ids") {
        value.forEach((item, i) => {
          if (item.type || item.number || item.image || item.expiry_date) {
            fd.append(`ids[${i}][type]`, item.type || '');
            fd.append(`ids[${i}][number]`, item.number || '');
            
            // Only append image if it's a new file
            if (item.image instanceof File) {
              fd.append(`ids[${i}][image]`, item.image);
            } else if (item.image && isUrl(item.image)) {
              // If it's a full URL, extract just the filename
              const url = new URL(item.image);
              const pathParts = url.pathname.split('/');
              const filename = pathParts[pathParts.length - 1];
              fd.append(`ids[${i}][image]`, filename);
            } else if (item.image) {
              // If it's just a filename, use it as is
              fd.append(`ids[${i}][image]`, item.image);
            }
            
            if (item.expiry_date) fd.append(`ids[${i}][expiry_date]`, item.expiry_date);
            
            // Include the ID if it exists (for updates)
            if (item.id) {
              fd.append(`ids[${i}][id]`, item.id);
            }
          }
        });
      }

      
      
      
      else if (key === "profile_image") {
        // Only append profile_image if it's a new file
        if (value && value instanceof File) {
          fd.append("profile_image", value);
        } else if (value === null) {
          // Explicitly set to null if profile image was removed
          fd.append("profile_image", '');
        }
      } else if (key === 'dob' || key === 'joining_date') {
        // Format the date to ensure it's in the correct format
        let dateValue = value;
        if (value) {
          // If it's a Date object, format it, otherwise use as is
          dateValue = moment(value).isValid() ? moment(value).format('YYYY-MM-DD') : value;
        }
        fd.append(key, dateValue || '');
      } else if (key === 'pf_enabled') {
        fd.append('pf_enabled', value ? 1 : 0); // send as 1 or 0
      } else if (value !== null && value !== undefined && value !== '') {
        console.log(key, value)
        // Only append other fields if they have values
        fd.append(key, value);
        console.log(fd)
      }
    });

    try {
      setLoading(true);
      setErrors({});
      if (isEdit) {
        await updateEmployee(id, fd);
        showToast("Employee updated!");
      } else {
        await createEmployee(fd);
        showToast("Employee created!");
      }
      navigate("/employees");
    } catch (err) {
      // Map SQL unique constraint error to emp_code field if present
      let backendErrors = err.response?.data?.errors || {};
      if (
        err.response?.data?.message &&
        err.response.data.message.includes('Duplicate entry') &&
        err.response.data.message.includes('employees_emp_code_unique')
      ) {
        backendErrors.emp_code = [
          'The employee code has already been taken.'
        ];
      }
      if (Object.keys(backendErrors).length > 0) {
        setErrors(backendErrors);
        showToast("Validation errors occurred.", "danger");
      } else {
        showToast("Something went wrong.", "danger");
      }
    } finally {
      setLoading(false);
    }
  };
  

  return (
    <Layout>
      <TopBar />

      <ToastContainer position="top-end" className="p-3">
        <Toast show={toast.show} bg={toast.variant} onClose={() => setToast({ ...toast, show: false })}>
          <Toast.Body className="text-white">{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>

      <Container fluid className="px-3" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        <Form onSubmit={handleSubmit} className="small">
          <div 
            className="d-flex justify-content-between align-items-center py-2 mb-2 bg-white"
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 1000,
              borderBottom: '1px solid #e9ecef',
              margin: '0 -1rem',
              padding: '0.5rem 1rem'
            }}
          >
            <h5 className="m-0">{isEdit ? 'Edit Employee' : 'Add New Employee'}</h5>
            <Button 
              type="submit" 
              variant="primary" 
              size="sm"
              disabled={loading} 
              className="d-flex align-items-center"
            >
              {loading ? (
                <Spinner as="span" animation="border" size="sm" className="me-1" />
              ) : (
                <FaSave className="me-1" />
              )}
              {isEdit ? 'Update' : 'Save'}
            </Button>
          </div>
          <Row className="g-2">
            <Col md={5}>
              <Card className="mb-2">
                <Card.Header className="py-2"><h6 className="m-0">Basic Information</h6></Card.Header>
                <Card.Body className="p-3">
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small fw-bold mb-1">Profile</Form.Label>
                        {form.profile_image && (
                          <div className="mb-2">
                            <Image 
                              src={
                                form.profile_image instanceof File
                                  ? URL.createObjectURL(form.profile_image)
                                  : `${imageUrl}/${form.profile_image}`
                              }
                              alt="Profile"
                              style={{ maxWidth: '100px', maxHeight: '100px' }}
                              className="img-thumbnail"
                            />
                          </div>
                        )}
                        <Form.Control 
                          type="file" 
                          onChange={handleFileChange} 
                          accept="image/*"
                        />
                      </Form.Group>
                 
                    </Col>

                    <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control name="name" value={form.name} onChange={handleChange} isInvalid={!!errors.name} />
                        <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>


                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Employee Code</Form.Label>
                        <Form.Control name="emp_code"  value={form.emp_code} onChange={handleChange} isInvalid={!!errors.emp_code} />
                        <Form.Control.Feedback type="invalid">
                          {Array.isArray(errors.emp_code) ? errors.emp_code[0] : errors.emp_code}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Work Email</Form.Label>
                        <Form.Control 
                          name="email" 
                          type="email" 
                          value={form.email} 
                          onChange={handleChange} 
                          isInvalid={!!errors.email} 
                          placeholder="work@example.com"
                        />
                        <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Personal Email</Form.Label>
                        <Form.Control 
                          name="personal_email" 
                          type="email" 
                          value={form.personal_email || ''} 
                          onChange={handleChange} 
                          isInvalid={!!errors.personal_email}
                          placeholder="personal@example.com"
                        />
                        <Form.Control.Feedback type="invalid">{errors.personal_email}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Phone</Form.Label>
                        <Form.Control name="phone" value={form.phone} onChange={handleChange} isInvalid={!!errors.phone} />
                        <Form.Control.Feedback type="invalid">{errors.phone}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                     <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Designation</Form.Label>
                        {loadingDesignations ? (
                          <div className="d-flex align-items-center gap-2">
                            <Spinner size="sm" animation="border" role="status" />
                            <span>Loading designations...</span>
                          </div>
                        ) : (
                          <Form.Select name="designation_id" value={form.designation_id} onChange={handleChange} isInvalid={!!errors.designation_id}>
                            <option value="">Select Designation</option>
                            {designations.map((dept) => (
                              <option key={dept.id} value={dept.id}>
                                {dept.name}
                              </option>
                            ))}
                          </Form.Select>
                        )}
                        <Form.Control.Feedback type="invalid">{errors.designation_id}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Department</Form.Label>
                        {loadingDepartments ? (
                          <div className="d-flex align-items-center gap-2">
                            <Spinner size="sm" animation="border" role="status" />
                            <span>Loading departments...</span>
                          </div>
                        ) : (
                          <Form.Select name="department_id" value={form.department_id} onChange={handleChange} isInvalid={!!errors.department_id}>
                            <option value="">Select Department</option>
                            {departments.map((dept) => (
                              <option key={dept.id} value={dept.id}>
                                {dept.name}
                              </option>
                            ))}
                          </Form.Select>
                        )}
                        <Form.Control.Feedback type="invalid">{errors.department_id}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Country</Form.Label>
                        {loadingCountries ? (
                          <div className="d-flex align-items-center gap-2">
                            <Spinner size="sm" animation="border" role="status" />
                            <span>Loading countries...</span>
                          </div>
                        ) : (
                          <Form.Select name="country_id" value={form.country_id} onChange={handleChange} isInvalid={!!errors.country_id}>
                            <option value="">Select Country</option>
                            {countries.map((country) => (
                              <option key={country.id} value={country.id}>
                                {country.name}
                              </option>
                            ))}
                          </Form.Select>
                        )}
                        <Form.Control.Feedback type="invalid">{errors.country_id}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Employee Type</Form.Label>
                        <Form.Select 
                          name="employee_type" 
                          value={form.employee_type} 
                          onChange={handleChange}
                          isInvalid={!!errors.employee_type}
                        >
                          <option value="">Please Select</option>
                          <option value="Intern">Intern</option>
                          <option value="Trainee">Trainee</option>
                          <option value="Probation">Probation</option>
                          <option value="Contract">Contract</option>
                          <option value="Permanent">Permanent</option>
                          <option value="Freelancer">Freelancer</option>
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">{errors.employee_type}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>



                    {(form.employee_type !== 'Permanent') && (
                      <>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Duration</Form.Label>
                            <div className="d-flex">
                              <Form.Control
                                type="number"
                                min="1"
                                name="value"
                                value={form.duration_value || ''}
                                onChange={handleDurationChange}
                                className="me-2"
                                isInvalid={!!errors.duration_value}
                              />
                              <Form.Select
                                name="unit"
                                value={form.duration_unit || 'months'}
                                onChange={handleDurationChange}
                                style={{ minWidth: '100px' }}
                                isInvalid={!!errors.duration_unit}
                              >
                                <option value="days">Days</option>
                                <option value="months">Months</option>
                                <option value="years">Years</option>
                              </Form.Select>
                            </div>
                            <Form.Control.Feedback type="invalid">
                              {errors.duration_value || errors.duration_unit}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Expiry Date</Form.Label>
                            <Form.Control 
                              type="date" 
                              name="expiry_date" 
                              value={form.expiry_date} 
                              onChange={handleChange}
                              isInvalid={!!errors.expiry_date}
                            />
                      
                            <Form.Control.Feedback type="invalid">{errors.expiry_date}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </>
                    )}
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label className="small fw-bold mb-1">Date of Birth</Form.Label>
                        <Form.Control 
                          type="date" 
                          name="dob" 
                          size="sm"
                          value={form.dob}
                          onChange={handleChange} 
                          isInvalid={!!errors.dob}
                          className="small"
                        />
                        <Form.Control.Feedback type="invalid" className="small">{errors.dob}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mb-3">
                    <Form.Label>Address</Form.Label>
                    <Form.Control name="address" value={form.address} onChange={handleChange} isInvalid={!!errors.address} />
                    <Form.Control.Feedback type="invalid">{errors.address}</Form.Control.Feedback>
                  </Form.Group>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Joining Date</Form.Label>
                        <Form.Control 
                          type="date" 
                          name="joining_date" 
                          size="sm"
                          value={form.joining_date || ''}
                          onChange={handleChange} 
                          isInvalid={!!errors.joining_date}
                          className="small"
                        />
                      
                        <Form.Control.Feedback type="invalid" className="small">{errors.joining_date}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <Form.Select name="status" value={form.status} onChange={handleChange}>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Notice Period Start Date</Form.Label>
                        <Form.Control
                          type="date"
                          name="notice_period_start_date"
                          value={form.notice_period_start_date || ''}
                          onChange={handleChange}
                          isInvalid={!!errors.notice_period_start_date}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.notice_period_start_date}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Notice Period (Days)</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          name="notice_period_days"
                          value={form.notice_period_days}
                          onChange={handleChange}
                          isInvalid={!!errors.notice_period_days}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.notice_period_days}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                 
                  </Row>
                </Card.Body>
              </Card>
            </Col>

            <Col md={7}>
              <Card className="mb-4">
                <Card.Header><p className="table-txt-header m-0">ID Details</p></Card.Header>
                <Card.Body>
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th>ID Type</th>
                        <th>ID Number</th>
                        <th>ID Image</th>
                        <th>Expiry Date</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.ids.map((id, index) => (
                        <tr key={index}>
                          <td>
                            <Form.Select name="type" value={id.type} onChange={(e) => handleIDChange(index, e)}>
                              <option value="">Select ID</option>
                              <option value="pan">Pan</option>
                              <option value="aadhar">Aadhar</option>
                              <option value="passbook">Pass Book</option>
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Control name="number" value={id.number} onChange={(e) => handleIDChange(index, e)} />
                          </td>
                          <td>
                            <div className="d-flex flex-column align-items-center">
                              <div className="position-relative mb-2">
                                <div className="border rounded p-2 d-flex flex-column align-items-center" style={{ width: '120px', minHeight: '100px' }}>
                                  {id.image ? (
                                    id.fileType === 'application/pdf' ? (
                                      <div className="d-flex flex-column align-items-center justify-content-center h-100">
                                        <FaFilePdf className="text-danger mb-2" size={36} />
                                        <small className="text-center text-truncate w-100">
                                          {id.fileName || 'Document.pdf'}
                                        </small>
                                      </div>
                                    ) : (
                                      <>
                                        <Image 
                                          src={
                                            id.image instanceof File ? 
                                            URL.createObjectURL(id.image) : 
                                            id.image && id.image.startsWith('http') ? 
                                            id.image : 
                                            `${imageUrl}/${id.image}`
                                          } 
                                          alt="ID Document" 
                                          style={{ 
                                            maxWidth: '100px', 
                                            maxHeight: '80px',
                                            objectFit: 'contain'
                                          }} 
                                          className="img-thumbnail"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextElementSibling.style.display = 'flex';
                                          }}
                                        />
                                        <div 
                                          className="d-none align-items-center justify-content-center"
                                          style={{
                                            width: '100px',
                                            height: '80px',
                                            backgroundColor: '#f8f9fa',
                                            border: '1px solid #dee2e6',
                                            borderRadius: '0.25rem'
                                          }}
                                        >
                                          <FaImage className="text-muted" />
                                        </div>
                                      </>
                                    )
                                  ) : (
                                    <div className="d-flex flex-column align-items-center justify-content-center" style={{ height: '80px' }}>
                                      <FaUpload className="mb-2 text-muted" />
                                      <small className="text-muted text-center">Upload File</small>
                                      <small className="text-muted text-center small">(PDF/Image)</small>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="w-100">
                                <Form.Control 
                                  id={`id-image-${index}`}
                                  name="image" 
                                  type="file" 
                                  onChange={(e) => handleIDChange(index, e)} 
                                  accept="image/*,.pdf"
                                  className="d-none"
                                />
                                <Button 
                                  variant="outline-primary" 
                                  size="sm" 
                                  className="w-100"
                                  onClick={() => document.getElementById(`id-image-${index}`).click()}
                                >
                                  <FaCamera className="me-1" />
                                  {id.image ? 'Change' : 'Upload'}
                                </Button>
                              </div>
                            </div>
                          </td>
                          <td>
                            <Form.Control type="date" name="expiry_date" value={id.expiry_date} onChange={(e) => handleExpiryDateChange(index, e)} />
                          </td>
                          <td className="text-center">
                            <Button variant="outline-danger" size="sm" onClick={() => removeID(index)}>
                              <FaTimes />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <Button variant="secondary" size="sm" onClick={addID}>+ Add Another ID</Button>
                </Card.Body>
              </Card>
              
              {/* Salary Details Section */}
              <Card className="mb-4">
                <Card.Header><p className="table-txt-header m-0">Salary Details</p></Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Salary Type</Form.Label>
                        <Form.Select 
                          name="salary_type" 
                          value={form.salary_type || ''} 
                          onChange={handleChange}
                          disabled={true}
                        >
                          <option value="base">Monthly Salary</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Base Salary</Form.Label>
                        <Form.Control 
                          type="number" 
                          name="base_salary" 
                          value={form.base_salary || ''} 
                          onChange={handleChange}
                          placeholder="e.g., 5000"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    {/* <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Hourly Rate</Form.Label>
                        <Form.Control 
                          type="number" 
                          name="hourly_rate" 
                          value={form.hourly_rate || ''} 
                          onChange={handleChange}
                          placeholder="e.g., 25"
                        />
                      </Form.Group>
                    </Col> */}
                    <Col md={6}>
                      <Form.Group className="mb-3">
                      <Form.Check 
                          type="switch"
                          id="pf-enabled-switch"
                          label="PF Enabled"
                          checked={form.pf_enabled || false}
                          onChange={(e) => setForm({...form, pf_enabled: e.target.checked})}
                        />
                        <Form.Text className="text-muted">
                          Enable if employee is eligible for Provident Fund
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                 
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Form>
      </Container>
    </Layout>
  );
};

export default EmployeeForm;
